import React, { useState } from 'react';
import { getOptimizedImageSrc } from '@/utils/webpConverter';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallbackSrc?: string;
  webpSrc?: string;
  loading?: 'lazy' | 'eager';
  sizes?: string;
  srcSet?: string;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  fallbackSrc,
  webpSrc,
  loading = 'lazy',
  sizes,
  srcSet,
  className,
  alt,
  ...props
}) => {
  const [hasWebPError, setHasWebPError] = useState(false);
  const [hasFallbackError, setHasFallbackError] = useState(false);

  const webpSource = webpSrc || getOptimizedImageSrc(src, fallbackSrc);
  const fallbackSource = fallbackSrc || src;

  const handleWebPError = () => {
    setHasWebPError(true);
  };

  const handleFallbackError = () => {
    setHasFallbackError(true);
  };

  // If both WebP and fallback fail, show original src
  if (hasWebPError && hasFallbackError) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        loading={loading}
        sizes={sizes}
        srcSet={srcSet}
        {...props}
      />
    );
  }

  return (
    <picture>
      {!hasWebPError && (
        <source
          srcSet={webpSource}
          type="image/webp"
          sizes={sizes}
        />
      )}
      <img
        src={hasWebPError ? fallbackSource : webpSource}
        alt={alt}
        className={className}
        loading={loading}
        sizes={sizes}
        srcSet={hasWebPError ? srcSet : undefined}
        onError={hasWebPError ? handleFallbackError : handleWebPError}
        {...props}
      />
    </picture>
  );
};